/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState, memo} from 'react'
import {createPortal} from 'react-dom'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {DateAno, DateCompleteBarra, DateMes} from '../../../../../utils/date'
import {getInvoicePDF, updateInvoice} from '../../../../../services/invoices'
import {useToast} from '../../../../components/Toats'
import {NotImported} from '../../../../components/NotInformat'
import {EmptyInvoicesState} from '../EmptyInvoicesState'
import {getConcessionaireImageSrc} from '../../../../unit-consumer/components/TableUnitConsumers/BoardUnitConsumers'

type Props = {
  invoices: any[]
  setMyInvoice: any
  setShowCreateAppModal: any
  setShowCreateAppModalDelete: any
  onOpenConcessionaire: (unitConsumer: any) => void
  onOpenUnitConsumer: (unitConsumer: any) => void
  onPaymentUpdated: () => void
}

const getNextDaysIso = (dateString: string, days: number) => {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return null
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

const getNextMonthIso = (dateString: string) => {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return null
  date.setMonth(date.getMonth() + 1)
  return date.toISOString()
}

const PaymentStatus = ({paid}: {paid: boolean}) => {
  const color = paid ? 'success' : 'danger'

  return (
    <span
      className={`d-inline-flex align-items-center gap-2 rounded-pill bg-light-${color} text-${color} fw-bold fs-7 ps-2 pe-3 py-1`}
      style={{whiteSpace: 'nowrap'}}
      title={paid ? 'Pagamento confirmado' : 'Pagamento pendente'}
    >
      <span
        className={`position-relative d-flex align-items-center justify-content-center rounded-circle bg-${color}`}
        style={{width: '1.9rem', height: '1.9rem'}}
      >
        <i className='bi bi-cash-coin text-white fs-5'></i>
        <i
          className={`bi ${
            paid ? 'bi-check-circle-fill' : 'bi-clock-fill'
          } text-${color} position-absolute bg-white rounded-circle`}
          style={{fontSize: '0.7rem', lineHeight: 1, bottom: '-0.15rem', right: '-0.2rem'}}
        ></i>
      </span>
      {paid ? 'Pago' : 'Pendente'}
    </span>
  )
}

type PaymentStatusSelectProps = {
  paid: boolean
  loading: boolean
  disabled: boolean
  onChange: (paid: boolean) => void
}

const PaymentStatusSelect = ({paid, loading, disabled, onChange}: PaymentStatusSelectProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{top: number; right: number} | null>(null)

  const close = () => setPosition(null)

  const toggle = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (position) return close()

    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    setPosition({top: rect.bottom + 4, right: window.innerWidth - rect.right})
  }

  useEffect(() => {
    if (!position) return

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      close()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position])

  const select = (event: React.MouseEvent, value: boolean) => {
    event.stopPropagation()
    close()
    onChange(value)
  }

  const options = [
    {value: true, label: 'Pago', icon: 'bi-check-circle-fill text-success'},
    {value: false, label: 'Pendente', icon: 'bi-clock-fill text-danger'},
  ]

  return (
    <>
      <button
        ref={buttonRef}
        type='button'
        className='btn p-0 border-0 bg-transparent'
        disabled={disabled || loading}
        title='Alterar status do pagamento'
        onClick={toggle}
      >
        {loading ? (
          <span className='spinner-border spinner-border-sm text-muted' />
        ) : (
          <PaymentStatus paid={paid} />
        )}
      </button>

      {position &&
        createPortal(
          <div
            ref={menuRef}
            onClick={(event) => event.stopPropagation()}
            className='bg-body shadow-sm rounded py-2'
            style={{
              position: 'fixed',
              top: position.top,
              right: position.right,
              minWidth: '9rem',
              zIndex: 1060,
            }}
          >
            {options.map((option) => (
              <button
                key={option.label}
                type='button'
                className={`btn btn-sm w-100 text-start d-flex align-items-center gap-2 px-4 py-2 rounded-0 bg-hover-light ${
                  option.value === paid ? 'fw-bold' : ''
                }`}
                onClick={(event) => select(event, option.value)}
              >
                <i className={`bi ${option.icon}`}></i>
                {option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  )
}

export const TableBody = memo(function TableBody({
  invoices,
  setMyInvoice,
  setShowCreateAppModal,
  setShowCreateAppModalDelete,
  onOpenConcessionaire,
  onOpenUnitConsumer,
  onPaymentUpdated,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [updatingPaymentId, setUpdatingPaymentId] = useState<string | null>(null)
  const [paymentOverrides, setPaymentOverrides] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setPaymentOverrides({})
  }, [invoices])
  const {toggleToast} = useToast()

  const getPDF = async (link_file: any) => {
    try {
      setLoading(true)
      const myPDF = await getInvoicePDF(link_file || '')
      const link = document.createElement('a')

      link.href = myPDF.url
      link.setAttribute('download', 'fatura.pdf')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.log(error)
      toggleToast('Fatura não encontrada.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleButtonClickUpdate = (event: React.MouseEvent, invoice: any) => {
    event.stopPropagation()

    const updatedInvoice = {
      ...invoice,
      idUnitConsumer: invoice.unitConsumer?.id || null,
      idConcessionarie: invoice.unitConsumer?.concessioraire?.id || null,
    }

    setMyInvoice(updatedInvoice)
    setShowCreateAppModal(true)
  }

  const handleChangePayment = async (invoice: any, paid: boolean) => {
    const current = (paymentOverrides[invoice.id] ?? invoice.confirm_payment) === true
    if (!invoice.id || current === paid) return

    try {
      setUpdatingPaymentId(invoice.id)
      await updateInvoice(invoice.id, {confirm_payment: paid})
      setPaymentOverrides((prev) => ({...prev, [invoice.id]: paid}))
      toggleToast(paid ? 'Pagamento confirmado.' : 'Pagamento marcado como pendente.', 'success')
      onPaymentUpdated()
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao atualizar o status do pagamento.', 'error')
    } finally {
      setUpdatingPaymentId(null)
    }
  }

  const handleButtonClickDelete =(event: React.MouseEvent, invoice: any) => {
    event.stopPropagation()
    setMyInvoice(invoice)
    setShowCreateAppModalDelete(true)
  }

  const handleButtonClickDownload = (event: React.MouseEvent, link_file: any) => {
    event.stopPropagation()
    getPDF(link_file)
  }

  return (
    <tbody>
      {invoices && invoices.length !== 0 ? (
        invoices.map((invoice: any, index: number) => {
          const keyId = invoice.id || index

          return (
            <tr key={keyId} onClick={() => setMyInvoice(invoice)}>
              <td>
                <div className='d-flex align-items-center'>
                  <div className='symbol symbol-45px me-5'>
                    <i className=' bi bi-upc fs-5hx '> </i>
                  </div>
                  <div className='d-flex justify-content-start flex-column'>
                    <a
                      href='#'
                      className='text-dark fw-bold text-hover-primary fs-6'
                      title={invoice.unitConsumer ? 'Editar unidade consumidora' : undefined}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        if (invoice.unitConsumer) onOpenUnitConsumer(invoice.unitConsumer)
                      }}
                    >
                      {invoice.unitConsumer?.name || (
                        <NotImported className='fs-6 text-gray-400 text-hover-primary' />
                      )}
                      {invoice.unitConsumer && (
                        <span className='d-inline-block' style={{verticalAlign: '-0.1em', marginLeft: '0.4rem'}}>
                          <KTSVG
                            path='/media/icons/duotune/general/gen001.svg'
                            className='svg-icon-7 svg-icon-muted'
                          />
                        </span>
                      )}
                    </a>
                    <span className='text-muted fw-semibold text-muted d-block fs-7'>
                      {invoice.unitConsumer?.contrato || ''}
                    </span>
                  </div>
                </div>
              </td>

              <td>
                <span
                  className={`badge badge-light-${
                    invoice.unitConsumer?.type_unit_consumer === 'GERADORA'
                      ? 'primary'
                      : invoice.unitConsumer?.type_unit_consumer === 'CONSUMIDORA'
                      ? 'info'
                      : 'secondary'
                  } fs-7 fw-semibold`}
                >
                  {invoice.unitConsumer?.type_unit_consumer || (
                    <NotImported className='fs-6 text-gray-400 text-hover-primary' />
                  )}
                </span>
              </td>

              <td>
                <a href='#' className='text-dark fw-bold text-hover-primary fs-6'>
                  {invoice.mes ? (
                    DateMes(getNextDaysIso(invoice.mes, 3))
                  ) : (
                    <NotImported className='fs-6 text-gray-400 text-hover-primary' />
                  )}
                </a>
                {invoice.mes && (
                  <span className='text-muted fw-semibold text-muted d-block fs-7'>
                    {DateAno(invoice.mes)}
                  </span>
                )}
              </td>

              <td>
                <a href='#' className='text-dark fw-bold text-hover-primary fs-6'>
                  <i style={{marginRight: '0.5rem'}} className='bi bi bi-calendar-event'></i>
                  {invoice.leitura_atual ? (
                    DateCompleteBarra(getNextDaysIso(invoice.leitura_atual, 0))
                  ) : (
                    <NotImported className='fs-6 text-gray-400 text-hover-primary' />
                  )}
                </a>
              </td>

              <td>
                <a href='#' className='text-dark fw-bold text-hover-primary fs-6'>
                  <i style={{marginRight: '0.5rem'}} className='bi bi bi-calendar-event'></i>
                  {invoice.leitura_proxima ? (
                    DateCompleteBarra(getNextDaysIso(invoice.leitura_proxima, 0))
                  ) : invoice.leitura_atual ? (
                    DateCompleteBarra(getNextMonthIso(invoice.leitura_atual))
                  ) : (
                    <NotImported className='fs-6 text-gray-400 text-hover-primary' />
                  )}
                </a>
              </td>

              <td>
                <span
                  className={`badge badge-light-${
                    invoice.status === 'INFORMADO'
                      ? 'success'
                      : invoice.status === 'NÃO INFORMADO'
                      ? 'danger'
                      : 'secondary'
                  } fs-7 fw-semibold`}
                >
                  {invoice.status || (
                    <NotImported className='fs-6 text-gray-400 text-hover-primary' />
                  )}
                </span>
              </td>

              <td>
                <div className='d-flex align-items-center' style={{gap: '0.75rem'}}>
                  {invoice.unitConsumer?.concessioraire && (
                    <img
                      src={getConcessionaireImageSrc(
                        invoice.unitConsumer.concessioraire.link_imagem
                      )}
                      alt={invoice.unitConsumer.concessioraire.name}
                      style={{
                        width: '2.25rem',
                        height: '2.25rem',
                        objectFit: 'contain',
                        borderRadius: '0.5rem',
                        background: '#fff',
                        border: '1px solid var(--bs-gray-200)',
                        padding: '0.15rem',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <a
                    href='#'
                    className='text-dark fw-bold text-hover-primary fs-6'
                    title={
                      invoice.unitConsumer?.concessioraire ? 'Editar concessionária' : undefined
                    }
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      if (invoice.unitConsumer?.concessioraire)
                        onOpenConcessionaire(invoice.unitConsumer)
                    }}
                  >
                    {invoice.unitConsumer?.concessioraire?.name || (
                      <NotImported className='fs-6 text-gray-400 text-hover-primary' />
                    )}
                  </a>
                </div>
              </td>

              <td>
                <div
                  className={`mb-6 bg-${
                    invoice.unitConsumer?.status === 'ATIVA'
                      ? 'success'
                      : invoice.unitConsumer?.status === 'INATIVA'
                      ? 'danger'
                      : 'secondary'
                  } rounded-circle border border-4 border-white h-20px w-20px`}
                ></div>
              </td>

              <td>
                <div className='d-flex justify-content-end flex-shrink-0'>
                  <PaymentStatusSelect
                    paid={(paymentOverrides[invoice.id] ?? invoice.confirm_payment) === true}
                    loading={updatingPaymentId === invoice.id}
                    disabled={!invoice.id}
                    onChange={(paid) => handleChangePayment(invoice, paid)}
                  />
                </div>
              </td>

              <td>
                <div className='d-flex justify-content-end flex-shrink-0'>
                  <a
                    onClick={(event) => handleButtonClickUpdate(event, invoice)}
                    href='#'
                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                  >
                    <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                  </a>
                  <a
                    onClick={(event) => {
                      event.preventDefault()
                      if (invoice.link_file) {
                        handleButtonClickDownload(event, invoice.link_file)
                      } else {
                        toggleToast('Não há arquivo de fatura para download. ', 'alert')
                      }
                    }}
                    aria-disabled={loading || !invoice.link_file}
                    href='#'
                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                    style={{
                      pointerEvents: loading || !invoice.link_file ? 'none' : 'auto',
                      opacity: loading || !invoice.link_file ? 0.6 : 1,
                    }}
                  >
                    <KTSVG path='/media/icons/duotune/files/fil017.svg' className='svg-icon-3' />
                  </a>
                  {invoice.unitConsumer?.type_unit_consumer !== 'GERADORA' && (
                    <a
                      onClick={(event) => handleButtonClickDelete(event, invoice)}
                      href='#'
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen027.svg'
                        className='svg-icon-3'
                      />
                    </a>
                  )}
                </div>
              </td>
            </tr>
          )
        })
      ) : (
        <tr>
          <td colSpan={10}>
            <EmptyInvoicesState />
          </td>
        </tr>
      )}
    </tbody>
  )
})
